<div class="page">
  <div class="header">
    <h1 class="title">Benefits</h1>
    <div class="subtitle">Manage and assign benefits and snacks to your teams</div>
  </div>

  @if (!isInitialCompanyCheckComplete) {
    <section class="page-loading">
      <mat-spinner [diameter]="32"></mat-spinner>
      <span>Loading</span>
    </section>
  }

  @if (isInitialCompanyCheckComplete && !hasCompany && !isNativeCompanyManager) {
    <section class="setup-cta">
      <div class="setup-cta-content">
        <mat-icon>business</mat-icon>
        <div class="setup-cta-copy">
          <h2>Create your first company</h2>
          <p>To use Benefits, create a company first in Company > Company.</p>
        </div>
      </div>
      <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
        Go to Company
      </button>
    </section>
  }

  @if (isInitialCompanyCheckComplete && hasCompany) {
    <div class="top-tabs">
      <button type="button" class="top-tab" [class.active]="activeTopTab === 'benefits'" (click)="setTopTab('benefits')">
        <mat-icon>favorite_border</mat-icon>
        Assign Benefits
      </button>
      @if (showSnacksSection) {
        <button
          type="button"
          class="top-tab"
          [class.active]="activeTopTab === 'snacks'"
          (click)="setTopTab('snacks')"
          >
          <mat-icon>campaign</mat-icon>
          Assign Snacks
        </button>
      }
      @if (!isNativeCompanyManager) {
        <button
          type="button"
          class="top-tab"
          [class.active]="activeTopTab === 'contributions'"
          (click)="setTopTab('contributions')"
          >
          <mat-icon>attach_money</mat-icon>
          Assign Contributions
        </button>
      }
    </div>
    @switch (activeTopTab) {
      @case ('benefits') {
        @if (!isLoading) {
          <div class="summary">
            <div class="step">
              <span class="step-index">1</span>
              <span class="step-text">Choose which benefits to assign to teams</span>
              <span class="badge">{{ selectedBenefitsCount }} selected</span>
            </div>
            <div class="step">
              <span class="step-index">2</span>
              <span class="step-text">Choose which teams to assign the benefits to</span>
              <span class="badge">{{ selectedTeamsCount }} team@if (selectedTeamsCount !== 1) {
                <span>s</span>
              } selected</span>
            </div>
            <div class="summary-actions">
              <button
                mat-stroked-button
                type="button"
                [disabled]="isSaving || !hasSelections"
                (click)="clearSelections()"
                >
                Clear Selections
              </button>
              <button mat-raised-button color="primary" [disabled]="!canAssign" (click)="assignBenefitsToTeams()">
                Assign Benefits ({{ selectedBenefitsCount }} to {{ selectedTeamsCount }} team@if (selectedTeamsCount !== 1) {
                <span>s</span>
                })
              </button>
            </div>
          </div>
        }
        @if (isLoading) {
          <div class="loading">Loading benefits and teams...</div>
        }
        @if (!isLoading) {
          <div class="grid">
            <section class="panel benefits-panel">
              <div class="panel-header">
                <h2>Step 1: Benefits</h2>
                <mat-checkbox
                  [checked]="isAllBenefitsSelected"
                  [indeterminate]="isSomeBenefitsSelected"
                  (change)="onToggleAllBenefits($event)"
                  [disabled]="isSaving || !benefitProducts.length"
                  >
                  Select all
                </mat-checkbox>
              </div>
              @if (benefitProducts.length) {
                <div class="categories">
                  @for (category of categories; track trackCategory($index, category)) {
                    @if (getPopulatedBenefitSections(category.id); as sections) {
                      @if (sections.length > 0) {
                        <div class="category">
                          <h3>{{ category.title }}</h3>
                          @for (section of sections; track trackSection($index, section)) {
                            <div class="section">
                              <div class="section-title">{{ section.title }}</div>
                              <div class="product-list">
                                @for (product of section.products; track trackProduct($index, product)) {
                                  <div
                                    class="product"
                                    [class.product-disabled]="isBenefitProductPending(product)"
                                    [class.product-requestable]="isBenefitProductRequestable(product)"
                                    >
                                    <div
                                      class="product-header"
                                      (click)="onBenefitProductClicked(product)"
                                      >
                                      <mat-checkbox
                                        [checked]="isBenefitProductSelected(product.id)"
                                        [disabled]="!canInteractWithBenefitProductCheckbox(product)"
                                        (click)="$event.stopPropagation()"
                                        (change)="onBenefitProductCheckboxChanged(product, $event.checked)">
                                      </mat-checkbox>
                                      <div class="logo-wrap">
                                        @if (shouldShowBenefitProductImage(product)) {
                                          <img
                                            [src]="product.imageUrl"
                                            [alt]="(product.vendorName || product.productName || 'Benefit') + ' logo'"
                                            class="logo"
                                            loading="eager"
                                            decoding="async"
                                            referrerpolicy="no-referrer"
                                            (load)="onBenefitProductImageLoaded(product)"
                                            (error)="onBenefitProductImageError(product)"
                                            />
                                        }
                                        @if (shouldShowBenefitProductImagePlaceholder(product)) {
                                          <div class="logo-placeholder">
                                            {{ (product.vendorName || product.productName || '?').charAt(0) }}
                                          </div>
                                        }
                                      </div>
                                      <div class="product-copy">
                                        <div class="name">{{ product.vendorName || product.productName }}</div>
                                        <div class="description">{{ product.productName }}</div>
                                        @if (product.alwaysActive) {
                                          <div class="group-onboarding-label status-available">
                                            Always available for all teams
                                          </div>
                                        }
                                        @if (getBenefitProductStatusLabel(product)) {
                                          <div class="group-onboarding-label" [ngClass]="getBenefitProductStatusClass(product)">
                                            {{ getBenefitProductStatusLabel(product) }}
                                          </div>
                                        }
                                        @if (product.approvalDisabledReason) {
                                          <div class="group-onboarding-reason">
                                            {{ product.approvalDisabledReason }}
                                          </div>
                                        }
                                      </div>
                                      @if (product.detailsUrl) {
                                        <div class="product-actions">
                                          <button
                                            mat-stroked-button
                                            type="button"
                                            class="product-details-btn"
                                            (click)="openBenefitProductDetails(product, $event)"
                                            >
                                            View details
                                          </button>
                                        </div>
                                      }
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                          }
                        </div>
                      }
                    }
                  }
                </div>
              } @else {
                <div class="empty">No benefits are configured for this company yet.</div>
              }
            </section>
            <section class="panel teams-panel">
              <div class="panel-header">
                <h2>Step 2: Teams</h2>
              </div>
              @if (teams.length) {
                <div class="team-table">
                  <div class="team-row head">
                    <div class="col-select">
                      <mat-checkbox
                        [checked]="isAllTeamsSelected"
                        [indeterminate]="isSomeTeamsSelected"
                        (change)="onToggleAllTeams($event)"
                        [disabled]="isSaving"
                        >
                      </mat-checkbox>
                    </div>
                    <div class="col-name">Team Name</div>
                    <div class="col-members">Members</div>
                    <div class="col-action"></div>
                  </div>
                  @for (team of teams; track trackTeam($index, team)) {
                    <div class="team-row"
                      [class.selected]="isTeamSelected(team.id)"
                      (click)="toggleTeam(team.id)">
                      <div class="col-select">
                        <mat-checkbox
                          [checked]="isTeamSelected(team.id)"
                          [disabled]="isSaving"
                          (click)="$event.stopPropagation()"
                          (change)="onTeamToggle(team.id, $event.checked)">
                        </mat-checkbox>
                      </div>
                      <div class="col-name">{{ team.name }}</div>
                      <div class="col-members">{{ team.membersCount }}</div>
                      <div class="col-action">
                        <button
                          mat-stroked-button
                          type="button"
                          [disabled]="isSaving"
                          (click)="$event.stopPropagation(); loadBenefitSelectionsFromTeam(team)"
                          >
                          Load Benefits
                        </button>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="empty">No teams found for this company.</div>
              }
            </section>
          </div>
        }
      }
      @case ('snacks') {
        @if (!isLoading && !isLoadingSnackRewards) {
          <div class="summary">
            <div class="step">
              <span class="step-index">1</span>
              <span class="step-text">Choose which company-available snacks to enable for teams</span>
              <span class="badge">{{ selectedSnacksCount }} selected</span>
            </div>
            <div class="step">
              <span class="step-index">2</span>
              <span class="step-text">Choose which teams to assign the snacks to</span>
              <span class="badge">{{ selectedTeamsCount }} team@if (selectedTeamsCount !== 1) {
                <span>s</span>
              } selected</span>
            </div>
            <div class="summary-actions">
              <button
                mat-stroked-button
                type="button"
                [disabled]="isSaving || (!selectedSnacksCount && !selectedTeamsCount)"
                (click)="clearSnackSelections()"
                >
                Clear Selections
              </button>
              <button mat-raised-button color="primary" [disabled]="!canAssignSnacks" (click)="assignSnacksToTeams()">
                Assign Snacks ({{ selectedSnacksCount }} to {{ selectedTeamsCount }} team@if (selectedTeamsCount !== 1) {
                <span>s</span>
                })
              </button>
            </div>
          </div>
        }
        @if (isLoading || isLoadingSnackRewards) {
          <div class="loading">Loading snacks and teams...</div>
        }
        @if (!isLoading && !isLoadingSnackRewards) {
          <div class="grid">
            <section class="panel benefits-panel">
              <div class="panel-header">
                <h2>Step 1: Snacks</h2>
                <mat-checkbox
                  [checked]="isAllSnacksSelected"
                  [indeterminate]="isSomeSnacksSelected"
                  (change)="onToggleAllSnacks($event)"
                  [disabled]="isSaving || !snackRewards.length"
                  >
                  Select all
                </mat-checkbox>
              </div>
              @if (snackRewards.length) {
                <div class="categories">
                  <div class="category">
                    <div class="product-list">
                      @for (reward of snackRewards; track trackSnackReward($index, reward)) {
                        <div
                          class="product"
                          >
                          <div class="product-header" (click)="toggleSnackReward(reward.id)">
                            <mat-checkbox
                              [checked]="isSnackRewardSelected(reward.id)"
                              [disabled]="isSaving"
                              (click)="$event.stopPropagation()"
                              (change)="onSnackRewardToggle(reward.id, $event.checked)">
                            </mat-checkbox>
                            <div class="logo-wrap">
                              @if (reward.imageUrl) {
                                <img
                                  [src]="reward.imageUrl"
                                  [alt]="reward.title + ' image'"
                                  class="logo"
                                  loading="eager"
                                  decoding="async"
                                  referrerpolicy="no-referrer"
                                  />
                              }
                              @if (!reward.imageUrl) {
                                <div class="logo-placeholder">
                                  {{ (reward.title || '?').charAt(0) }}
                                </div>
                              }
                            </div>
                            <div class="product-copy">
                              <div class="name">{{ reward.title }}</div>
                              @if (reward.subtitle) {
                                <div class="description">{{ reward.subtitle }}</div>
                              }
                            </div>
                            <button
                              mat-icon-button
                              type="button"
                              matTooltip="Preview Explore panel"
                              [class.active-preview-button]="isSnackRewardPreviewed(reward.id)"
                              (click)="previewSnackReward(reward, $event)">
                              <mat-icon>visibility</mat-icon>
                            </button>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                  <div class="snack-preview-inline">
                    <app-snack-reward-preview [reward]="getPreviewSnackReward()"></app-snack-reward-preview>
                  </div>
                </div>
              } @else {
                <div class="empty">No snack rewards are available for this company.</div>
              }
            </section>
            <section class="panel teams-panel">
              <div class="panel-header">
                <h2>Step 2: Teams</h2>
              </div>
              @if (teams.length) {
                <div class="team-table">
                  <div class="team-row head">
                    <div class="col-select">
                      <mat-checkbox
                        [checked]="isAllTeamsSelected"
                        [indeterminate]="isSomeTeamsSelected"
                        (change)="onToggleAllTeams($event)"
                        [disabled]="isSaving"
                        >
                      </mat-checkbox>
                    </div>
                    <div class="col-name">Team Name</div>
                    <div class="col-members">Members</div>
                    <div class="col-action"></div>
                  </div>
                  @for (team of teams; track trackTeam($index, team)) {
                    <div class="team-row"
                      [class.selected]="isTeamSelected(team.id)"
                      (click)="toggleTeam(team.id)">
                      <div class="col-select">
                        <mat-checkbox
                          [checked]="isTeamSelected(team.id)"
                          [disabled]="isSaving"
                          (click)="$event.stopPropagation()"
                          (change)="onTeamToggle(team.id, $event.checked)">
                        </mat-checkbox>
                      </div>
                      <div class="col-name">{{ team.name }}</div>
                      <div class="col-members">{{ team.membersCount }}</div>
                      <div class="col-action">
                        <button
                          mat-stroked-button
                          type="button"
                          [disabled]="isSaving"
                          (click)="$event.stopPropagation(); loadSnackSelectionsFromTeam(team)"
                          >
                          Load Snacks
                        </button>
                      </div>
                    </div>
                  }
                </div>
              } @else {
                <div class="empty">No teams found for this company.</div>
              }
            </section>
          </div>
        }
      }
      @default {
        <section class="contributions-upsell">
          <app-coming-soon-upsell
            featureKey="payroll-assign-contributions"
            title="Assign Contributions is coming soon"
            description="We are actively building contribution assignment tools so you can manage employer and employee contribution settings by team."
            [renderMode]="'expanded'"
            [allowFeedback]="true"
            [context]="{ source: 'payroll-benefits-page', tab: 'assign-contributions' }"
            >
          </app-coming-soon-upsell>
        </section>
      }
    }
  }
</div>
